import { useEffect, useRef, useState } from 'react'
import type { MediaListItem } from '../../api/entries'
import { listUserMedia } from '../../api/entries'
import { AuthenticatedMedia } from './AuthenticatedMedia'

interface Props {
  username: string
  onSelect: (item: MediaListItem) => void
  onUpload: () => void
  onClose: () => void
}

export function MediaPicker({ username, onSelect, onUpload, onClose }: Props) {
  const [items, setItems] = useState<MediaListItem[]>([])
  const [loading, setLoading] = useState(true)
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    listUserMedia(username)
      .then((r) => setItems(r.items))
      .finally(() => setLoading(false))
  }, [username])

  function handleOverlayClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === overlayRef.current) onClose()
  }

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div style={{ background: 'var(--color-bg)', border: '1px solid var(--color-fg)', padding: '1rem', width: '90%', maxWidth: 560, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <span style={{ fontWeight: 'bold' }}>add media</span>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--color-fg)', cursor: 'pointer', fontSize: '1.2rem', lineHeight: 1, padding: '0 0.2rem' }}>×</button>
        </div>
        <button
          type="button"
          onClick={onUpload}
          style={{ width: 'auto', alignSelf: 'flex-start', marginBottom: '0.75rem' }}
        >
          upload new file
        </button>
        <div style={{ borderTop: '1px solid var(--color-fg)', opacity: 0.3, marginBottom: '0.75rem' }} />
        <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', opacity: 0.6 }}>pick from library</p>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading && <p style={{ opacity: 0.5, fontSize: '0.85rem' }}>loading…</p>}
          {!loading && items.length === 0 && (
            <p style={{ opacity: 0.5, fontSize: '0.85rem' }}>no uploaded files yet</p>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onSelect(item)}
                style={{
                  background: 'none',
                  border: '1px solid var(--color-fg)',
                  color: 'var(--color-fg)',
                  cursor: 'pointer',
                  padding: '0.4rem',
                  width: 100,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.25rem',
                }}
              >
                <div style={{ width: 80, height: 80, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {item.mimetype.startsWith('image/') ? (
                    <AuthenticatedMedia url={item.url} mimetype={item.mimetype} name={item.filename} />
                  ) : (
                    <span style={{ opacity: 0.5, fontSize: '0.7rem', textAlign: 'center', wordBreak: 'break-all' }}>{item.mimetype}</span>
                  )}
                </div>
                <span style={{ fontSize: '0.7rem', wordBreak: 'break-all', textAlign: 'center', maxWidth: '100%' }}>{item.filename}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
